import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Container, Paper, Box, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert, List, ListItem, ListItemText, IconButton, TextField, ListItemAvatar, Avatar, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Button, FormControlLabel, Switch, Tooltip } from '@mui/material';
import { doc, getDoc, updateDoc, collection, getDocs, writeBatch, arrayUnion, deleteDoc, deleteField, onSnapshot, query, serverTimestamp, where, orderBy, limit } from 'firebase/firestore';
import { db, storage, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import ParticipantTeamModal from '../../components/ParticipantTeamModal';
import { page } from '../../theme/commonStyles';
import { useAuth } from '../../context/AuthContext';
import { useAuction } from '../../context/AuctionContext';
import { useNotification } from '../../context/NotificationContext';
import { SPELFASES, REFUNDED_TRANSFER_TYPES } from '../../constants/game';

function AdminOverview() {
    const { auctionId } = useParams();
    // De veiling houdt hier zijn eigen, ruwe document bij; spelfase daarop is de
    // eventuele afwijking. De standaard komt van het seizoen via de context.
    const { seasonSpelfase } = useAuction();
    const navigate = useNavigate();
    const { impersonateUser } = useAuth();
    const notify = useNotification();
    const [auction, setAuction] = useState(null);
    const [requests, setRequests] = useState([]);
    const [impersonating, setImpersonating] = useState(false);
    const [participants, setParticipants] = useState([]);
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [spelfase, setSpelfase] = useState('');
    const [startBudget, setStartBudget] = useState(100);
    const [isOpen, setIsOpen] = useState(false);
    const [status, setStatus] = useState('Live');
    const [transfermarketClosed, setTransfermarketClosed] = useState(false);
    const [backgroundImageFile, setBackgroundImageFile] = useState(null);
    const [isUploadingBackground, setIsUploadingBackground] = useState(false);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [hasActiveTransferRound, setHasActiveTransferRound] = useState(false);

    const getTotalAllowedTransfers = useCallback((phase) => {
        const preAuction = ['Voorbereiding', 'Veiling', 'Blinde Pool'];
const preKnockout = ['Poulefase', 'Tussenronde', 'Draft'];
    const knockout = ['Knock-out', 'Finaleronde'];

        if (preAuction.includes(phase)) {
            return 12;
        }
        if (preKnockout.includes(phase)) {
            return 15;
        }
        if (knockout.includes(phase)) {
            return 19;
        }
        return 0; // Default for other phases
    }, []);

    const fetchAuctionAndRequests = useCallback(async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'auctions', auctionId);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const auctionData = docSnap.data();
                setAuction(auctionData);
                setSpelfase(auctionData.spelfase || '');
                setStartBudget(auctionData.startBudget === undefined ? 100 : auctionData.startBudget);
                setIsOpen(auctionData.isOpen || false);
                setStatus(auctionData.status || 'Live');
                setTransfermarketClosed(auctionData.transfermarketClosed || false);

                const requestsRef = collection(db, 'auctions', auctionId, 'joinRequests');
                const querySnapshot = await getDocs(requestsRef);
                const requestsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setRequests(requestsData);
            } else {
                setError('Veiling niet gevonden.');
            }
        } catch (err) {
            setError('Fout bij het ophalen van de veilinggegevens.');
            console.error(err);
        }
        setLoading(false);
    }, [auctionId]);

    useEffect(() => {
        fetchAuctionAndRequests();

        if (!auctionId) return;

        const participantsQuery = query(collection(db, 'auctions', auctionId, 'participants'));
        const unsubParticipants = onSnapshot(participantsQuery, (snapshot) => {
            const fetchedParticipants = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setParticipants(fetchedParticipants);
        });

        const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'));
        const unsubTransfers = onSnapshot(transfersQuery, (snapshot) => {
            const fetchedTransfers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTransfers(fetchedTransfers);
        });

        // Listener voor actieve transferronde (blokkeert switch)
        const transferRoundsQuery = query(
            collection(db, 'auctions', auctionId, 'transferRounds'),
            where('status', '==', 'open')
        );
        const unsubTransferRounds = onSnapshot(transferRoundsQuery, (snapshot) => {
            setHasActiveTransferRound(!snapshot.empty);
        });

        return () => {
            unsubParticipants && unsubParticipants();
            unsubTransfers && unsubTransfers();
            unsubTransferRounds && unsubTransferRounds();
        };
    }, [fetchAuctionAndRequests, auctionId]);

    const participantData = useMemo(() => {
        const totalAllowed = getTotalAllowedTransfers(auction?.spelfase || seasonSpelfase);

        return participants.map(p => {
            const playersMap = new Map();
            const sortedTransfers = [...transfers].sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));
            
            const incomingTransfers = [];
            sortedTransfers.forEach(t => {
                if (t.to === p.uid) {
                    playersMap.set(t.playerId, true);
                    incomingTransfers.push(t);
                }
                if (t.from === p.uid) {
                    playersMap.delete(t.playerId);
                }
            });
            const playersCount = playersMap.size;
            const spent = incomingTransfers.reduce((sum, t) => sum + t.amount, 0);

            // Correctly calculate used and remaining transfers, excluding coach-change en Draft (initiële teamopbouw)
            const excludedTypes = ['coach-change', 'Draft'];
            const transfersUsed = incomingTransfers.filter(t => !excludedTypes.includes(t.type)).length;
            const nonClTransfersCount = sortedTransfers.filter(t => t.from === p.uid && REFUNDED_TRANSFER_TYPES.includes(t.type)).length;
            const correctTransfersLeft = totalAllowed - transfersUsed + nonClTransfersCount;

            return { 
                ...p, 
                spent, 
                playersCount, 
                extraBudget: p.extraBudget || 0, 
                remainingBudget: p.budget,
                transfersUsed: transfersUsed, // Pass for handleSpelfaseChange
                transfersLeft: correctTransfersLeft // Overwrite with correct real-time value for display
            };
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [participants, transfers, auction?.spelfase, seasonSpelfase, getTotalAllowedTransfers]);

    const totals = useMemo(() => {
        return participantData.reduce((acc, p) => {
            acc.playersCount += p.playersCount;
            acc.transfersLeft += (p.transfersLeft ?? 0);
            acc.spent += p.spent;
            acc.extraBudget += p.extraBudget;
            acc.remainingBudget += p.remainingBudget;
            return acc;
        }, {
            playersCount: 0,
            transfersLeft: 0,
            spent: 0,
            extraBudget: 0,
            remainingBudget: 0,
        });
    }, [participantData]);

    const handleSpelfaseChange = async (event) => {
        const newSpelfase = event.target.value;
        if (newSpelfase === (auction.spelfase || '')) return;

        const oldSpelfase = auction.spelfase || seasonSpelfase;
        setSpelfase(newSpelfase);

        // Een lege keuze betekent "volg het seizoen": het eigen veld verdwijnt,
        // waarna de standaard van het seizoen geldt. De rest van de logica
        // hieronder werkt met de fase die daarna daadwerkelijk van kracht is.
        const effectiefNieuw = newSpelfase || seasonSpelfase;

        try {
            const batch = writeBatch(db);
            const auctionRef = doc(db, 'auctions', auctionId);
            batch.update(auctionRef, {
                spelfase: newSpelfase === '' ? deleteField() : newSpelfase,
            });

            const totalAllowed = getTotalAllowedTransfers(effectiefNieuw);
            // Deelnemers worden hier al bijgewerkt met de juiste 'transfersUsed' waarde, die alle inkomende transfers telt.
            participantData.forEach(p => {
                const transfersDone = p.transfersUsed; // Use the correctly calculated transfersUsed
                const newTransfersLeft = totalAllowed - transfersDone;
                const participantRef = doc(db, 'auctions', auctionId, 'participants', p.id);
                batch.update(participantRef, { transfersLeft: newTransfersLeft });
            });

            // --- Draft spelfase overgang logica ---
            if (effectiefNieuw === 'Draft') {
                // 1. Maak finalRankingIntermediateRound aan als kopie van liveRankingIntermediateRound
                const liveRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingIntermediateRound');
                const liveRankingIntermediateSnap = await getDoc(liveRankingIntermediateRef);

                if (liveRankingIntermediateSnap.exists()) {
                    const finalRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingIntermediateRound');
                    batch.set(finalRankingIntermediateRef, {
                        ...liveRankingIntermediateSnap.data(),
                        closedAt: serverTimestamp()
                    });
                }

                // 2. Bepaal draft deelnemers en uitgeschakelde deelnemers
                const finalRankingPouleRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRankingPoule');
                const finalRankingPouleSnap = await getDoc(finalRankingPouleRef);

                let draftParticipantUids = [];
                let eliminatedParticipantUids = [];

                if (finalRankingPouleSnap.exists() && liveRankingIntermediateSnap.exists()) {
                    const pouleRanking = finalRankingPouleSnap.data().ranking || [];
                    const intermediateRanking = liveRankingIntermediateSnap.data().ranking || [];

                    // Top 15 uit poulefase
                    const top15Uids = pouleRanking.slice(0, 15).map(p => p.uid);

                    // Nummer 1 van tussenronde
                    const sortedIntermediate = [...intermediateRanking].sort((a, b) => {
                        if (b.matchPoints !== a.matchPoints) return b.matchPoints - a.matchPoints;
                        const aScore = (a.playerPoints || 0) + (a.tacticPoints || 0) + (a.tacticCounter || 0);
                        const bScore = (b.playerPoints || 0) + (b.tacticPoints || 0) + (b.tacticCounter || 0);
                        return bScore - aScore;
                    });
                    const intermediateWinnerUid = sortedIntermediate.length > 0 ? sortedIntermediate[0].uid : null;

                    draftParticipantUids = [...top15Uids];
                    if (intermediateWinnerUid && !draftParticipantUids.includes(intermediateWinnerUid)) {
                        draftParticipantUids.push(intermediateWinnerUid);
                    }

                    // Alle deelnemers die niet in de draft zitten
                    const allParticipantUids = participants.map(p => p.uid || p.id);
                    eliminatedParticipantUids = allParticipantUids.filter(uid => !draftParticipantUids.includes(uid));
                }

                // 3. Maak draft config document aan
                const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
                batch.set(draftConfigRef, {
                    draftParticipants: draftParticipantUids,
                    eliminatedParticipants: eliminatedParticipantUids,
                    draftOrder: [],
                    currentPickIndex: 0,
                    status: 'not_started',
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });

                // 4. Transfermarkt sluiten
                batch.update(auctionRef, { transfermarketClosed: true });
                setTransfermarketClosed(true);
            }

            // --- Overgang naar Knock-out: uitschakeling transfers ---
            if (effectiefNieuw === 'Knock-out' && oldSpelfase === 'Draft') {
                // Haal draft config op om uitgeschakelde deelnemers te bepalen
                const draftConfigRef = doc(db, 'auctions', auctionId, 'draft', 'config');
                const draftConfigSnap = await getDoc(draftConfigRef);

                if (draftConfigSnap.exists()) {
                    const eliminatedUids = draftConfigSnap.data().eliminatedParticipants || [];

                    // Haal alle transfers op om te bepalen welke spelers bij uitgeschakelde deelnemers horen
                    const transfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');
                    const transfersSnap = await getDocs(query(transfersRef, orderBy('timestamp', 'asc')));

                    // Bouw per deelnemer hun huidige team op
                    const teamsByParticipant = new Map();
                    eliminatedUids.forEach(uid => teamsByParticipant.set(uid, new Map()));

                    transfersSnap.docs.forEach(transferDoc => {
                        const transfer = transferDoc.data();
                        // Voeg speler toe aan team van ontvanger
                        if (teamsByParticipant.has(transfer.to)) {
                            teamsByParticipant.get(transfer.to).set(String(transfer.playerId), transfer.playerName);
                        }
                        // Verwijder speler uit team van verzender
                        if (teamsByParticipant.has(transfer.from)) {
                            teamsByParticipant.get(transfer.from).delete(String(transfer.playerId));
                        }
                    });

                    // Maak uitschakeling transfers aan voor alle spelers van uitgeschakelde deelnemers
                    const auctionTransfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');
                    for (const [participantUid, playersMap] of teamsByParticipant.entries()) {
                        for (const [playerId, playerName] of playersMap.entries()) {
                            const transferRef = doc(auctionTransfersRef);
                            batch.set(transferRef, {
                                playerId: playerId,
                                playerName: playerName,
                                from: participantUid,
                                to: 'vrije-markt',
                                amount: 0,
                                type: 'Uitschakeling tussenronde',
                                timestamp: serverTimestamp()
                            });
                        }
                    }
                }
            }

            // --- Overgang naar Finaleronde: bepaal winnaars knock-out en schakel verliezers uit ---
            if (effectiefNieuw === 'Finaleronde' && oldSpelfase === 'Knock-out') {
                const participantsByUid = new Map(participants.map(p => [p.uid || p.id, p]));
                const participantsByName = new Map(participants.map(p => [p.name, p.uid || p.id]));

                const scheduleSnap = await getDocs(collection(db, 'auctions', auctionId, 'schedule'));
                const knockoutAchtsteRounds = scheduleSnap.docs
                    .map(scheduleDoc => ({ id: scheduleDoc.id, ...scheduleDoc.data() }))
                    .filter(round => round.type === 'knockout' && round.stageName === '8th Finals')
                    .sort((a, b) => {
                        const legA = parseInt((a.leg || '0/2').split('/')[0], 10) || 0;
                        const legB = parseInt((b.leg || '0/2').split('/')[0], 10) || 0;
                        return legA - legB;
                    });

                if (knockoutAchtsteRounds.length < 2) {
                    throw new Error('Finaleronde kan niet gestart worden: minder dan 2 Knock-out rondes (8th Finals) gevonden.');
                }

                const leg1 = knockoutAchtsteRounds[0];
                const leg2 = knockoutAchtsteRounds[1];

                const leg1RankingSnap = await getDoc(doc(db, 'auctions', auctionId, 'roundRankings', leg1.id));
                const leg2RankingSnap = await getDoc(doc(db, 'auctions', auctionId, 'roundRankings', leg2.id));

                if (!leg1RankingSnap.exists() || !leg2RankingSnap.exists()) {
                    throw new Error('Finaleronde kan niet gestart worden: roundRankings voor Knock-out rondes ontbreken.');
                }

                const leg1Results = new Map((leg1RankingSnap.data().results || []).map(r => [r.uid, r]));
                const leg2Results = new Map((leg2RankingSnap.data().results || []).map(r => [r.uid, r]));

                const leg1Matches = leg1.matches || [];
                if (leg1Matches.length === 0) {
                    throw new Error('Finaleronde kan niet gestart worden: geen wedstrijden gevonden in Knock-out ronde 1.');
                }

                const findReturnMatch = (match) => {
                    return (leg2.matches || []).find(m =>
                        (m.home === match.home && m.away === match.away) ||
                        (m.home === match.away && m.away === match.home)
                    );
                };

                const winnerUids = [];

                for (const match of leg1Matches) {
                    const returnMatch = findReturnMatch(match);
                    if (!returnMatch) {
                        throw new Error(`Finaleronde kan niet gestart worden: return match ontbreekt voor ${match.home} - ${match.away}.`);
                    }

                    const homeUid = participantsByName.get(match.home);
                    const awayUid = participantsByName.get(match.away);
                    if (!homeUid || !awayUid) {
                        throw new Error(`Finaleronde kan niet gestart worden: deelnemer niet gevonden voor ${match.home} - ${match.away}.`);
                    }

                    const homeLeg1Data = leg1Results.get(homeUid);
                    const awayLeg1Data = leg1Results.get(awayUid);
                    const homeLeg2Data = leg2Results.get(homeUid);
                    const awayLeg2Data = leg2Results.get(awayUid);

                    const homeLeg1 = (homeLeg1Data?.playerPoints ?? null) !== null ? (homeLeg1Data.playerPoints || 0) + (homeLeg1Data.tacticPoints || 0) + (homeLeg1Data.tacticCounter || 0) : null;
                    const awayLeg1 = (awayLeg1Data?.playerPoints ?? null) !== null ? (awayLeg1Data.playerPoints || 0) + (awayLeg1Data.tacticPoints || 0) + (awayLeg1Data.tacticCounter || 0) : null;
                    const homeLeg2 = (homeLeg2Data?.playerPoints ?? null) !== null ? (homeLeg2Data.playerPoints || 0) + (homeLeg2Data.tacticPoints || 0) + (homeLeg2Data.tacticCounter || 0) : null;
                    const awayLeg2 = (awayLeg2Data?.playerPoints ?? null) !== null ? (awayLeg2Data.playerPoints || 0) + (awayLeg2Data.tacticPoints || 0) + (awayLeg2Data.tacticCounter || 0) : null;

                    const hasIncompleteData = [homeLeg1, awayLeg1, homeLeg2, awayLeg2].some(v => typeof v !== 'number');
                    if (hasIncompleteData) {
                        throw new Error(`Finaleronde kan niet gestart worden: onvolledige scoredata voor ${match.home} - ${match.away}.`);
                    }

                    const homeTotal = homeLeg1 + homeLeg2;
                    const awayTotal = awayLeg1 + awayLeg2;

                    if (homeTotal > awayTotal) {
                        winnerUids.push(homeUid);
                    } else if (awayTotal > homeTotal) {
                        winnerUids.push(awayUid);
                    } else if (homeLeg2 > awayLeg2) {
                        winnerUids.push(homeUid);
                    } else if (awayLeg2 > homeLeg2) {
                        winnerUids.push(awayUid);
                    } else {
                        throw new Error(`Finaleronde kan niet gestart worden: gelijkstand blijft gelijk na tie-break in ronde 2 voor ${match.home} - ${match.away}.`);
                    }
                }

                const uniqueWinnerUids = Array.from(new Set(winnerUids));
                const allParticipantUids = participants.map(p => p.uid || p.id);
                const eliminatedUids = allParticipantUids.filter(uid => !uniqueWinnerUids.includes(uid));

                const existingEliminationQuery = query(
                    collection(db, 'auctions', auctionId, 'auctionTransfers'),
                    where('type', '==', 'Uitschakeling Knock-out ronde'),
                    limit(1)
                );
                const existingEliminationSnap = await getDocs(existingEliminationQuery);
                const eliminationAlreadyDone = !existingEliminationSnap.empty;

                if (!eliminationAlreadyDone && eliminatedUids.length > 0) {
                    const transfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');
                    const transfersSnap = await getDocs(query(transfersRef, orderBy('timestamp', 'asc')));

                    const teamsByParticipant = new Map();
                    eliminatedUids.forEach(uid => teamsByParticipant.set(uid, new Map()));

                    transfersSnap.docs.forEach(transferDoc => {
                        const transfer = transferDoc.data();
                        if (teamsByParticipant.has(transfer.to)) {
                            teamsByParticipant.get(transfer.to).set(String(transfer.playerId), transfer.playerName);
                        }
                        if (teamsByParticipant.has(transfer.from)) {
                            teamsByParticipant.get(transfer.from).delete(String(transfer.playerId));
                        }
                    });

                    const auctionTransfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');
                    for (const [participantUid, playersMap] of teamsByParticipant.entries()) {
                        for (const [playerId, playerName] of playersMap.entries()) {
                            const transferRef = doc(auctionTransfersRef);
                            batch.set(transferRef, {
                                playerId,
                                playerName,
                                from: participantUid,
                                to: 'vrije-markt',
                                amount: 0,
                                type: 'Uitschakeling Knock-out ronde',
                                timestamp: serverTimestamp()
                            });
                        }
                    }
                }

                const finalRoundParticipantsRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRoundParticipants');
                const finalRoundParticipants = uniqueWinnerUids.map(uid => {
                    const participant = participantsByUid.get(uid);
                    return {
                        uid,
                        name: participant?.name || 'Onbekend'
                    };
                });
                batch.set(finalRoundParticipantsRef, {
                    participants: finalRoundParticipants,
                    participantUids: uniqueWinnerUids,
                    source: 'Knock-out 8th Finals',
                    sourceRoundIds: [leg1.id, leg2.id],
                    updatedAt: serverTimestamp()
                }, { merge: true });

                const liveRankingFinalRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingFinaleronde');
                const initialFinalRanking = finalRoundParticipants.map(p => ({
                    uid: p.uid,
                    name: p.name,
                    matchPoints: 0,
                    playerPoints: 0,
                    tacticPoints: 0,
                    tacticCounter: 0,
                    wins: 0,
                    draws: 0,
                    losses: 0,
                }));
                batch.set(liveRankingFinalRef, {
                    ranking: initialFinalRanking,
                    lastUpdated: serverTimestamp(),
                }, { merge: true });
            }

            // --- Overgang terug naar Knock-out vanuit Finaleronde: reset finaleronde uitschakeling data ---
            if (effectiefNieuw === 'Knock-out' && oldSpelfase === 'Finaleronde') {
                const transfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');
                const eliminationTransfersQuery = query(
                    transfersRef,
                    where('type', '==', 'Uitschakeling Knock-out ronde')
                );
                const eliminationTransfersSnap = await getDocs(eliminationTransfersQuery);
                eliminationTransfersSnap.docs.forEach(transferDoc => {
                    batch.delete(transferDoc.ref);
                });

                batch.delete(doc(db, 'auctions', auctionId, 'ranking', 'finalRoundParticipants'));
                batch.delete(doc(db, 'auctions', auctionId, 'ranking', 'liveRankingFinaleronde'));
            }

            // --- Overgang terug naar Draft vanuit Knock-out: verwijder uitschakeling transfers ---
            if (effectiefNieuw === 'Draft' && oldSpelfase === 'Knock-out') {
                const transfersRef = collection(db, 'auctions', auctionId, 'auctionTransfers');
                const eliminationTransfersQuery = query(
                    transfersRef,
                    where('type', '==', 'Uitschakeling tussenronde')
                );
                const eliminationTransfersSnap = await getDocs(eliminationTransfersQuery);
                eliminationTransfersSnap.docs.forEach(transferDoc => {
                    batch.delete(transferDoc.ref);
                });
            }

            await batch.commit();

            // --- Overgang naar Veiling: scout-aantallen vastzetten op de spelers ---
            // In de Veiling ligt de scoutlijst vast. Een Cloud Function telt per speler
            // hoe vaak hij gescout is en zet dat als `scoutCount` op elk auctionPlayers-
            // document, zodat de projectiekaart het direct kan tonen. Server-side omdat
            // de rules de admin de scoutlijsten van deelnemers niet laten lezen.
            if (effectiefNieuw === 'Veiling') {
                const computeScoutCounts = httpsCallable(functions, 'computeScoutCounts');
                await computeScoutCounts({ auctionId });
            }
        } catch (err) {
            setError('Fout bij het bijwerken van de spelfase.');
            console.error(err);
        }
    };

    const handleStartBudgetChange = async () => {
        const newBudgetValue = Number(startBudget);
        if (startBudget === '' || isNaN(newBudgetValue) || newBudgetValue < 0) {
            setStartBudget(auction?.startBudget === undefined ? 100 : auction.startBudget);
            return;
        }

        const oldBudgetValue = auction?.startBudget === undefined ? 100 : auction.startBudget;
        const budgetDifference = newBudgetValue - oldBudgetValue;

        if (budgetDifference === 0) return; // No change

        try {
            const batch = writeBatch(db);
            const auctionRef = doc(db, 'auctions', auctionId);
            batch.update(auctionRef, { startBudget: newBudgetValue });

            participants.forEach(p => {
                const participantRef = doc(db, 'auctions', auctionId, 'participants', p.id);
                const newParticipantBudget = (p.budget || 0) + budgetDifference;
                batch.update(participantRef, { budget: newParticipantBudget });
            });

            await batch.commit();
            setAuction(prev => ({ ...prev, startBudget: newBudgetValue }));

        } catch (err) {
            setError('Fout bij het bijwerken van het startbudget.');
            console.error(err);
        }
    };

    const handleIsOpenChange = async (event) => {
        const newIsOpen = event.target.checked;
        setIsOpen(newIsOpen);
        try {
            const docRef = doc(db, 'auctions', auctionId);
            await updateDoc(docRef, {
                isOpen: newIsOpen
            });
        } catch (err) {
            setError('Fout bij het bijwerken van de veilingstatus.');
            console.error(err);
        }
    };

    const handleStatusChange = async (event) => {
        const newStatus = event.target.value;
        setStatus(newStatus);
        try {
            const docRef = doc(db, 'auctions', auctionId);
            await updateDoc(docRef, {
                status: newStatus
            });
        } catch (err) {
            setError('Fout bij het bijwerken van de veilingstatus.');
            console.error(err);
        }
    };

    const handleTransfermarketClosedChange = async (event) => {
        const newIsClosed = event.target.checked;
        setTransfermarketClosed(newIsClosed);
        try {
            const docRef = doc(db, 'auctions', auctionId);
            await updateDoc(docRef, {
                transfermarketClosed: newIsClosed
            });
            // Update local auction state to avoid waiting for a full refetch
            setAuction(prev => ({ ...prev, transfermarketClosed: newIsClosed }));
        } catch (err) {
            setError('Fout bij het bijwerken van de transfermarkt status.');
            console.error(err);
        }
    };

    const handleExtraBudgetChange = async (participantId, newExtraBudgetString) => {
        const participant = participants.find(p => p.id === participantId);
        if (!participant) return;

        const oldExtraBudget = participant.extraBudget || 0;
        const newExtraBudget = Number(newExtraBudgetString);

        if (newExtraBudgetString === '' || isNaN(newExtraBudget) || newExtraBudget < 0) {
            // Invalid input, do not update. The input field will keep the invalid value until re-render.
            return;
        }

        if (oldExtraBudget === newExtraBudget) return; // No change

        const budgetDifference = newExtraBudget - oldExtraBudget;
        const newRemainingBudget = participant.budget + budgetDifference;

        try {
            const participantRef = doc(db, 'auctions', auctionId, 'participants', participantId);
            await updateDoc(participantRef, {
                extraBudget: newExtraBudget,
                budget: newRemainingBudget
            });
        } catch (err) {
            setError('Fout bij het bijwerken van het extra budget.');
            console.error(err);
        }
    };

    const handleUploadBackgroundImage = async () => {
        if (!backgroundImageFile) return;

        setIsUploadingBackground(true);
        setError('');

        try {
            const storageRef = ref(storage, `auctions/${auctionId}/backgroundImage`);
            await uploadBytes(storageRef, backgroundImageFile);
            const downloadUrl = await getDownloadURL(storageRef);

            const auctionRef = doc(db, 'auctions', auctionId);
            await updateDoc(auctionRef, {
                backgroundUrl: downloadUrl
            });

            setAuction(prev => ({...prev, backgroundUrl: downloadUrl}));
            setBackgroundImageFile(null);

        } catch (err) {
            setError('Fout bij het uploaden van de afbeelding.');
            console.error(err);
        } finally {
            setIsUploadingBackground(false);
        }
    };

    const handleAccept = async (request) => {
        try {
            const batch = writeBatch(db);
            const auctionRef = doc(db, 'auctions', auctionId);
            const participantRef = doc(db, 'auctions', auctionId, 'participants', request.uid);
            const requestRef = doc(db, 'auctions', auctionId, 'joinRequests', request.id);

            const initialTransfers = getTotalAllowedTransfers(auction.spelfase || seasonSpelfase);

            // 1. Add the user to the participants subcollection
            batch.set(participantRef, {
                uid: request.uid,
                email: request.email,
                name: request.name,
                budget: auction.startBudget === undefined ? 100 : auction.startBudget,
                extraBudget: 0,
                transfersLeft: initialTransfers
            });

            // 2. Add user's UID to the participantUids array
            batch.update(auctionRef, { participantUids: arrayUnion(request.uid) });

            // 3. Delete the join request
            batch.delete(requestRef);

            await batch.commit();

            setRequests(prevRequests => prevRequests.filter(r => r.id !== request.id));
        } catch (err) {
            setError('Fout bij het accepteren van het verzoek.');
            console.error(err);
        }
    };

    const handleDecline = async (requestId) => {
        try {
            const requestRef = doc(db, 'auctions', auctionId, 'joinRequests', requestId);
            await deleteDoc(requestRef);
            setRequests(prevRequests => prevRequests.filter(r => r.id !== requestId));
        } catch (err) {
            setError('Fout bij het afwijzen van het verzoek.');
            console.error(err);
        }
    };

    const handleRowClick = (participant) => {
        setSelectedParticipant(participant);
    };

    const handleCloseModal = () => {
        setSelectedParticipant(null);
    };

    const handleImpersonate = async (participant) => {
        setImpersonating(true);
        try {
            await impersonateUser(participant.uid, auctionId);
            // Na succesvolle impersonation, redirect naar de team pagina van de veiling
            navigate(`/auction/${auctionId}/my-team`);
        } catch (err) {
            console.error('Impersonation failed:', err);
            notify.error(err.message || 'Inloggen als andere gebruiker mislukt.');
        } finally {
            setImpersonating(false);
        }
    };

    if (loading) {
        return (
            <Container sx={page.loadingContainer}><CircularProgress /></Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Alert severity="error" sx={{ mt: 4 }}>{error}</Alert>
            </Container>
        );
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Overzicht voor {auction?.name}
            </Typography>

            {/* Deelnameverzoeken Sectie */}
            {requests.length > 0 && (
                <Paper elevation={6} sx={{ padding: 3, mb: 4 }}>
                    <Typography component="h2" variant="h5" gutterBottom>
                        Deelnameverzoeken
                    </Typography>
                    <List >
                        {requests.map(request => (
                            <ListItem key={request.id} secondaryAction={
                                <>
                                    <IconButton edge="end" aria-label="accept" onClick={() => handleAccept(request)}>
                                        <CheckIcon />
                                    </IconButton>
                                    <IconButton edge="end" aria-label="decline" onClick={() => handleDecline(request.id)}>
                                        <CloseIcon />
                                    </IconButton>
                                </>
                            }>
                                <ListItemAvatar>
                                    <Avatar>{(request.name || request.email || 'U').charAt(0)}</Avatar>
                                </ListItemAvatar>
                                <ListItemText primary={request.name || 'Onbekende naam'} secondary={request.email} />
                            </ListItem>
                        ))}
                    </List>
                </Paper>
            )}

            {/* Deelnemers Sectie */}
            <Paper elevation={6} sx={{ padding: 3, mb: 4 }}>
                <Typography component="h2" variant="h5" gutterBottom>
                    Deelnemers
                </Typography>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Naam</TableCell>
                                <TableCell align="right">Aantal spelers</TableCell>
                                <TableCell align="right">Resterende transfers</TableCell>
                                <TableCell align="right">Uitgegeven</TableCell>
                                <TableCell align="right">Extra budget</TableCell>
                                <TableCell align="right">Resterend budget</TableCell>
                                <TableCell align="center">Acties</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {participantData.map((p) => (
                                <TableRow key={p.id} hover onClick={() => handleRowClick(p)} sx={{ cursor: 'pointer' }}>
                                    <TableCell component="th" scope="row">{p.name}</TableCell>
                                    <TableCell align="right">{p.playersCount}</TableCell>
                                    <TableCell align="right">{p.transfersLeft ?? 0}</TableCell>
                                    <TableCell align="right">€{p.spent}</TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <TextField
                                            type="number"
                                            size="small"
                                            sx={{ width: '100px' }}
                                            defaultValue={p.extraBudget}
                                            onBlur={(e) => handleExtraBudgetChange(p.id, e.target.value)}
                                            InputProps={{ startAdornment: <Typography sx={{ mr: 0.5 }}>€</Typography> }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">€{p.remainingBudget}</TableCell>
                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title={`Inloggen als ${p.name}`}>
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleImpersonate(p)}
                                                disabled={impersonating}
                                            >
                                                <LoginIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                            }
                        </TableBody>
                        <TableFooter>
                            <TableRow sx={{ '& > *': { fontWeight: 'bold', borderTop: '2px solid black' } }}>
                                <TableCell component="th" scope="row">Totaal</TableCell>
                                <TableCell align="right">{totals.playersCount}</TableCell>
                                <TableCell align="right">{totals.transfersLeft}</TableCell>
                                <TableCell align="right">€{totals.spent}</TableCell>
                                <TableCell align="right">€{totals.extraBudget}</TableCell>
                                <TableCell align="right">€{totals.remainingBudget}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Paper >

            {/* Instellingen Sectie */}
            <Paper elevation={6} sx={{ padding: 3 }}>
                <Typography component="h2" variant="h5" gutterBottom>
                    Instellingen
                </Typography>
                <TextField
                    label="Naam van de veiling"
                    variant="outlined"
                    value={auction?.name || ''}
                    disabled
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="status-select-label">Status</InputLabel>
                    <Select
                        labelId="status-select-label"
                        id="status-select"
                        value={status}
                        label="Status"
                        onChange={handleStatusChange}
                    >
                        <MenuItem value="Test">Test</MenuItem>
                        <MenuItem value="Live">Live</MenuItem>
                    </Select>
                </FormControl>
                <FormControlLabel
                    control={<Switch checked={isOpen} onChange={handleIsOpenChange} />}
                    label="Open veiling"
                    sx={{ mb: 1, display: 'block' }}
                />
                <TextField
                    label="Start budget"
                    variant="outlined"
                    type="number"
                    value={startBudget}
                    onChange={(e) => setStartBudget(e.target.value)}
                    onBlur={handleStartBudgetChange}
                    InputProps={{
                        inputProps: { min: 0 }
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="spelfase-select-label">Spelfase</InputLabel>
                    <Select
                        labelId="spelfase-select-label"
                        id="spelfase-select"
                        value={spelfase}
                        label="Spelfase"
                        onChange={handleSpelfaseChange}
                    >
                        <MenuItem value="">
                            <em>Volg het seizoen{seasonSpelfase ? ` (${seasonSpelfase})` : ''}</em>
                        </MenuItem>
                        {SPELFASES.map((fase) => (
                            <MenuItem key={fase} value={fase}>{fase}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    {spelfase
                        ? 'Deze subleague wijkt af van het seizoen. Kies "Volg het seizoen" om weer mee te bewegen.'
                        : `De administrator zet de fase voor het hele seizoen${seasonSpelfase ? '' : ', maar heeft er nog geen gekozen'}.`}
                </Typography>
                <FormControlLabel
                    control={
                        <Switch 
                            checked={transfermarketClosed} 
                            onChange={handleTransfermarketClosedChange}
                            disabled={(auction?.spelfase || seasonSpelfase) === 'Draft' || hasActiveTransferRound}
                        />
                    }
                    label={
                        hasActiveTransferRound
                            ? 'Transfermarkt gesloten (actieve transferronde)'
                            : (auction?.spelfase || seasonSpelfase) === 'Draft'
                                ? 'Transfermarkt gesloten (verplicht tijdens Draft)'
                                : 'Transfermarkt gesloten'
                    }
                    sx={{ mt: 2, display: 'block' }}
                />
                <Box sx={{ mt: 4 }}>
                    <Typography component="h3" variant="h6" gutterBottom>
                        Achtergrond veilingpagina
                    </Typography>
                    {auction?.backgroundUrl && !backgroundImageFile && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption">Huidige achtergrond:</Typography>
                            <Box
                                component="img"
                                src={auction.backgroundUrl}
                                alt="Huidige achtergrond"
                                sx={{
                                    width: '100%',
                                    maxWidth: '300px',
                                    display: 'block',
                                    mt: 1,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}
                            />
                        </Box>
                    )}
                    <Button variant="contained" component="label" disabled={isUploadingBackground}>
                        Kies afbeelding
                        <input type="file" hidden accept="image/*" onChange={(e) => setBackgroundImageFile(e.target.files[0])} />
                    </Button>
                    {backgroundImageFile && (
                        <Typography variant="body2" sx={{ display: 'inline-block', ml: 2, fontStyle: 'italic' }}>
                            {backgroundImageFile.name}
                        </Typography>
                    )}
                    {backgroundImageFile && (
                        <Button
                            variant="outlined"
                            onClick={handleUploadBackgroundImage}
                            disabled={isUploadingBackground}
                            sx={{ ml: 2 }}
                        >
                            {isUploadingBackground ? <CircularProgress size={24} /> : 'Upload en bewaar'}
                        </Button>
                    )}
                </Box>
            </Paper>

            {selectedParticipant && (
                <ParticipantTeamModal
                    open={!!selectedParticipant}
                    onClose={handleCloseModal}
                    auctionId={auctionId}
                    participant={selectedParticipant}
                    seasonId={auction?.season}
                />
            )}

        </Box>
    );
}

export default AdminOverview;
